// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, type ReactNode } from 'react';

import { Text, Card, Title, LoadingOverlay, Stack, Space } from '@mantine/core';
import { v4 as uuid } from 'uuid';

import { CollapsibleDescription } from 'components/Mantine/DetailPage/CollapsibleDescription';
import { EditButton } from 'components/Mantine/DetailPage/EditButton';
import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { TextareaWrapped } from 'components/Mantine/TextInputWrapped';
import { isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';

export const DESCRIPTION_MAX_LENGTH = 4096;

type Props = {
  title?: ReactNode;
  description: string;
  updateDescription: (description: string) => Promise<void>;
  editable: boolean;
};

export const DescriptionEditor = ({ title, description, editable, updateDescription }: Props) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(description);

  const [saving, setSaving] = useState(false);
  const onSubmit = () => {
    if (value === description) {
      setEditing(false);
      return;
    }
    setSaving(true);
    void updateDescription(value)
      .then(() => setEditing(false))
      .finally(() => setSaving(false));
  };

  const [descriptionEditorId] = useState(() => uuid());
  useBlockerWhen(`description-editor-${descriptionEditorId}`, editing);

  return (
    <Card withBorder>
      {typeof title === 'string' ? <Title order={2}>{title}</Title> : title}
      {isNotNil(title) && <Space mt="xs" />}
      {editing ? (
        <Stack>
          <TextareaWrapped
            autoFocus
            value={value}
            onChange={setValue}
            onSubmit={onSubmit}
            minRows={5}
            disabled={saving}
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
          <ModalButtons
            onConfirm={onSubmit}
            onClose={() => setEditing(false)}
            hideDivider
            disabled={value === description}
          />
        </Stack>
      ) : description ? (
        <CollapsibleDescription description={description} />
      ) : (
        <Text c="dimmed">No description set.</Text>
      )}
      {editable && (
        <EditButton
          onClick={() => {
            if (!editing) {
              setEditing(true);
              setValue(description);
            }
          }}
          editing={editing}
        >
          Edit
        </EditButton>
      )}
      <LoadingOverlay visible={saving} />
    </Card>
  );
};
